/**
 * Regras derivadas da viagem — a regra de genericidade em código executável.
 *
 * Nada aqui conhece a viagem de referência: nem o destino, nem as datas, nem a
 * duração, nem quantas pessoas ou famílias existem, nem a moeda.
 * Toda viagem é definida por nome, destino, data de início, data de fim e
 * fuso; tudo o mais é derivado.
 *
 * docs/history/ROADMAP_ORLANDO_2026.md §1 (histórico); regra vigente em INVARIANTES.md.
 */

import {
  type CalendarDate,
  compareCalendarDates,
  diffCalendarDays,
  isWithinRange,
} from './calendar.js';
import { resolveToday, type UtcOffsetSchedule } from './timezone.js';

/**
 * Estado explícito da viagem, definido por ação do dono.
 * Distinto da FASE, que é derivada das datas.
 */
export type TripStatus = 'upcoming' | 'active' | 'finished' | 'archived';

/** Fase da Home. Derivada — nunca persistida. */
export type TripPhase = 'antes' | 'durante' | 'depois';

/** O mínimo que define uma viagem. */
export interface TripDates {
  readonly startDate: CalendarDate;
  readonly endDate: CalendarDate;
  /** Fuso IANA da viagem, ex.: "America/New_York". Não é o fuso do aparelho. */
  readonly timeZone: string;
  readonly status: TripStatus;
  /**
   * Tabela de offsets UTC da janela da viagem, enviada pelo servidor.
   * Usada quando o runtime não tem dados IANA confiáveis — ver `timezone.ts`.
   */
  readonly utcOffsetSchedule?: UtcOffsetSchedule;
}

export class InvalidTripDatesError extends Error {
  constructor(start: CalendarDate, end: CalendarDate) {
    super(`Fim da viagem (${end}) é anterior ao início (${start}).`);
    this.name = 'InvalidTripDatesError';
  }
}

export function assertValidTripDates(start: CalendarDate, end: CalendarDate): void {
  if (compareCalendarDates(end, start) < 0) throw new InvalidTripDatesError(start, end);
}

/**
 * Duração em dias, intervalo fechado.
 * Uma viagem que começa e termina no mesmo dia dura 1 dia, não 0.
 */
export function tripDurationInDays(trip: TripDates): number {
  assertValidTripDates(trip.startDate, trip.endDate);
  return diffCalendarDays(trip.endDate, trip.startDate) + 1;
}

/**
 * Número do dia dentro da viagem, base 1.
 * Fora do intervalo, devolve `null` — o chamador decide o que exibir.
 */
export function tripDayNumber(trip: TripDates, date: CalendarDate): number | null {
  if (!isWithinRange(date, trip.startDate, trip.endDate)) return null;
  return diffCalendarDays(date, trip.startDate) + 1;
}

/**
 * Fase da Home.
 *
 * Precedência decidida no roadmap §1.1: o encerramento explícito vence a data.
 * Sem isso, uma viagem encerrada no dia 15 de 22 voltaria sozinha para
 * "durante" no dia seguinte.
 */
export function tripPhase(trip: TripDates, now: Date = new Date()): TripPhase {
  if (trip.status === 'finished' || trip.status === 'archived') return 'depois';

  assertValidTripDates(trip.startDate, trip.endDate);
  const today = resolveToday(trip.timeZone, now, trip.utcOffsetSchedule);

  if (compareCalendarDates(today, trip.startDate) < 0) return 'antes';
  if (compareCalendarDates(today, trip.endDate) <= 0) return 'durante';
  return 'depois';
}

/**
 * Dias que faltam para começar. `0` no dia da partida, `null` se já começou.
 * É o que alimenta a contagem regressiva da Home "antes".
 */
export function daysUntilStart(trip: TripDates, now: Date = new Date()): number | null {
  const today = resolveToday(trip.timeZone, now, trip.utcOffsetSchedule);
  const delta = diffCalendarDays(trip.startDate, today);
  return delta >= 0 ? delta : null;
}
